import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const COST_SEG_IMG =
  "https://images.pexels.com/photos/6537735/pexels-photo-6537735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const services = [
  {
    eyebrow: "Service 01",
    title: "Cost Segregation",
    link: "/services/cost-segregation",
    summary:
      "Engineering-based studies that reclassify 20–35% of a building's basis from 39-year property into 5, 7, and 15-year property — unlocking accelerated and bonus depreciation in year one.",
    points: [
      "On new acquisitions, ground-up construction, renovations, and look-back studies",
      "IRS-compliant engineering reports prepared by our in-house engineers",
      "Audit defense and documentation included",
      "Works on assets placed in service in any prior year",
    ],
    image: COST_SEG_IMG,
  },
  {
    eyebrow: "Service 02",
    title: "R&D Tax Credit",
    link: "/services/rd-tax-credit",
    summary:
      "A dollar-for-dollar federal credit (plus most state credits) for the people who design, build, improve, and engineer. Manufacturing, software, AEC, life sciences, food & beverage, and more.",
    points: [
      "4-part qualification test, applied to every project and activity",
      "Wages, supplies, contractor expense, and cloud computing all considered",
      "Captures up to 3 prior open tax years",
      "Audit-ready technical narratives + time tracking methodology",
    ],
    image:
      "https://images.pexels.com/photos/8082233/pexels-photo-8082233.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32 bg-forest-950 text-ivory">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="mb-20 max-w-3xl">
          <p className="text-brass uppercase tracking-[0.24em] text-sm mb-4">
            Two strategies. Real cash.
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.05] tracking-tight">
            We do two things — <br />
            <span className="italic text-brass">and we do them
            extraordinarily well.</span>
          </h2>
        </div>

        <div className="space-y-24">
          {services.map((s, i) => (
            <div
              key={s.title}
              data-testid={`service-block-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`grid lg:grid-cols-12 gap-10 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6">
                <div className="relative overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-[420px] object-cover grayscale-[15%] hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute top-0 left-0 bg-brass text-white px-4 py-2 text-xs uppercase tracking-[0.2em]">
                    {s.eyebrow}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-6 lg:px-6">
                <h3 className="font-serif text-4xl sm:text-5xl text-ivory mb-6 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-ivory/80 text-lg leading-relaxed mb-8">
                  {s.summary}
                </p>
                <ul className="space-y-3 mb-8">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-3 text-ivory/80">
                      <span className="text-brass mt-1.5">▸</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={s.link}
                  className="inline-flex items-center gap-2 text-brass border-b border-brass/40 hover:border-brass pb-1 transition-all"
                  data-testid={`learn-more-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  Learn more about {s.title} <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
